import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
  Tooltip,
  Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';


const exampleQueries = {
  "Price Action": [
    { query: "1D CLOSE > 1D SMA(20)", description: "Above Moving Average" },
    { query: "1D ROC(1) > 5", description: "Price Up 5%" },
    { query: "1D HIGH > 1D HIGH[1]", description: "Higher High" },
    { query: "1D VOLUME > 1D SMA(20)", description: "Volume Spike" },
    { query: "1D OPEN > 1D CLOSE[1]", description: "Gap Up" }
  ],
  "Trend Following": [
    { query: "1D SMA(50) > 1D SMA(200) AND 1D CLOSE > 1D SMA(50)", description: "Golden Cross" },
    { query: "1D SMA(20) > 1D SMA(50) AND 1D SMA(50) > 1D SMA(200)", description: "Triple Moving Average" },
    { query: "1D CLOSE > 1D SMA(20) AND 1D SMA(20) > 1D SMA(50) AND 1D SMA(50) > 1D SMA(200)", description: "Strong Uptrend" },
    { query: "1D CLOSE > 1D SMA(50) AND 1D VOLUME > 1D SMA(50)", description: "Trend with Volume" }
  ],
  "Momentum": [
    { query: "1D RSI(14) > 70", description: "RSI Overbought" },
    { query: "1D RSI(14) < 30", description: "RSI Oversold" },
    { query: "1D RSI(14) > 60 AND 1D MACD(12,26,9) > 1D SIGNAL", description: "Strong Momentum" },
    { query: "1D MACD(12,26,9) > 1D SIGNAL AND 1D MACD(12,26,9) > 0", description: "MACD Bullish" }
  ],
  "Volume Analysis": [
    { query: "1D CLOSE > 1D SMA(20) AND 1D VOLUME > 1D SMA(20)", description: "High Volume Breakout" },
    { query: "1D CLOSE > 1D OPEN AND 1D VOLUME > 1D SMA(20)", description: "Accumulation" },
    { query: "1D SMA(10) > 1D SMA(50)", description: "Volume Trend" }
  ],
  "Volatility": [
    { query: "1D BBANDS_UPPER(20,2) - 1D BBANDS_LOWER(20,2) < 1D SMA(20)", description: "Bollinger Band Squeeze" },
    { query: "1D ATR(14) > 1D SMA(14)", description: "ATR Spike" },
    { query: "1D CLOSE > 1D BBANDS_UPPER(20,2) AND 1D BBANDS_UPPER(20,2) - 1D BBANDS_LOWER(20,2) < 1D SMA(20)", description: "Low Volatility Breakout" }
  ],
  "Oscillators": [
    { query: "1D RSI(14) < 30 AND 1D STOCH(14,3,3) < 20", description: "Double Oversold" },
    { query: "1D RSI(14) > 60 AND 1D STOCH(14,3,3) > 50 AND 1D CCI(20) > 100", description: "Momentum Confirmation" },
    { query: "1D MACD(12,26,9) > 1D SIGNAL AND 1D RSI(14) > 50 AND 1D STOCH(14,3,3) > 1D STOCHF(14,3)", description: "Triple Screen" }
  ],
  "Advanced": [
    { query: "1D CLOSE > 1D SMA(50) AND 1D SMA(20) > 1D SMA(50) AND 1D RSI(14) > 50 AND 1D MACD(12,26,9) > 1D SIGNAL AND 1D VOLUME > 1D SMA(20)", description: "Trend and Momentum" },
    { query: "1D CLOSE > 1D BBANDS_UPPER(20,2) AND 1D VOLUME > 1D SMA(20) AND 1D RSI(14) > 60 AND 1D CLOSE > 1D OPEN", description: "Volume Breakout" },
    { query: "1D RSI(14) < 30 AND 1D MACD(12,26,9) > 1D SIGNAL AND 1D STOCH(14,3,3) < 20 AND 1D VOLUME > 1D SMA(20)", description: "Reversal Pattern" },
    { query: "1D CLOSE > 1D SMA(20) AND 1D CLOSE > 1D SMA(50) AND 1D RSI(14) > 50 AND 1D VOLUME > 1D SMA(50) AND 1D ATR(14) > 1D SMA(14)", description: "Multi-Timeframe" }
  ],
  "Complete Analysis": [
    { query: `1D CLOSE > 1D SMA(20) AND 
        1D SMA(20) > 1D SMA(50) AND 
        1D SMA(50) > 1D SMA(200) AND 
        1D RSI(14) > 50 AND 
        1D MACD(12,26,9) > 1D SIGNAL AND 
        1D STOCH(14,3,3) > 20 AND 
        1D STOCH(14,3,3) < 80 AND 
        1D VOLUME > 1D SMA(20) AND 
        1D OBV > 1D SMA(10) AND 
        1D ATR(14) > 1D SMA(14) AND 
        1D CLOSE < 1D BBANDS_UPPER(20,2)`, description: "Full Technical Screen" }
  ]
};

const syntaxRules = [
  { rule: "Timeframe", example: "1MIN, 5MIN, 15MIN, 30MIN, 1H, 4H, 1D", description: "Specify the data interval" },
  { rule: "Indicator", example: "RSI(14), MACD(12,26,9)", description: "Technical indicator with parameters" },
  { rule: "Operator", example: "> < >= <= == !=", description: "Comparison operators" },
  { rule: "Logical", example: "AND OR", description: "Combine multiple conditions" },
  { rule: "Value", example: "50, CLOSE, HIGH, LOW", description: "Numeric values or price references" }
];

const IndicatorHelp = ({ onExampleClick }) => {
  return (
    <Box>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Query Syntax Guide</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {syntaxRules.map((rule, index) => (
              <Paper key={index} sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>{rule.rule}</Typography>
                <Typography variant="body2" color="text.secondary">{rule.description}</Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip label={rule.example} size="small" />
                </Box>
              </Paper>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Example Queries</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {Object.entries(exampleQueries).map(([category, queries]) => (
            <Box key={category} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>{category}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {queries.map((q, index) => (
                  <Tooltip key={index} title={q.description} arrow placement="top">
                    <Chip
                      label={q.query}
                      onClick={() => onExampleClick(q.query)}
                      variant="outlined"
                      sx={{ m: 0.5 }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Indicator Reference</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            
            <Typography variant="h6">Overlap Studies (Trend Indicators)</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ml: 2 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>SMA (Simple Moving Average)</Typography>
                <Typography variant="body2" paragraph>Calculates the arithmetic mean of prices over a specified period.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period (default=20)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D SMA(20), 1H CLOSE {'>'} 1H SMA(50)</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>EMA (Exponential Moving Average)</Typography>
                <Typography variant="body2" paragraph>Gives more weight to recent prices, reacting faster to price changes.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period (default=20)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D EMA(12) {'>'} 1D EMA(26), 1D CLOSE {'>'} 1D EMA(20)</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>WMA (Weighted Moving Average)</Typography>
                <Typography variant="body2" paragraph>Assigns different weights to data points, with higher weights for recent prices.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period (default=10)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D WMA(10) {'>'} 1D SMA(10)</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>DEMA (Double Exponential Moving Average)</Typography>
                <Typography variant="body2" paragraph>Reduces the lag in traditional moving averages.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period (default=21)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D DEMA(21) {'>'} 1D EMA(21)</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>TEMA (Triple Exponential Moving Average)</Typography>
                <Typography variant="body2" paragraph>Further reduces lag from EMA while maintaining smoothing.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period (default=14)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D TEMA(14) {'>'} 1D DEMA(14)</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>KAMA (Kaufman Adaptive Moving Average)</Typography>
                <Typography variant="body2" paragraph>Adapts to market volatility and changes speed based on price movement.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period (default=30)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D KAMA(30) {'>'} 1D SMA(30)</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>T3 (Triple Exponential Moving Average T3)</Typography>
                <Typography variant="body2" paragraph>Ultra-smooth moving average with minimal lag.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period (default=5)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D T3(5) {'>'} 1D T3(10)</Typography>
              </Paper>
            </Box>

            <Typography variant="h6">Bollinger Bands</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ml: 2 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>BBANDS (Bollinger Bands Middle)</Typography>
                <Typography variant="body2" paragraph>Shows the middle band (SMA) of Bollinger Bands.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period, nbdevup, nbdevdn (default=20,2,2)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D CLOSE {'>'} 1D BBANDS(20,2,2)</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>BBANDS_UPPER (Bollinger Bands Upper Band)</Typography>
                <Typography variant="body2" paragraph>Upper volatility band (SMA + standard deviations).</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period, nbdevup, nbdevdn (default=20,2,2)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D CLOSE {'>'} 1D BBANDS_UPPER(20,2,2)</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>BBANDS_MIDDLE (Bollinger Bands Middle Band)</Typography>
                <Typography variant="body2" paragraph>Same as simple moving average, explicit middle band.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period, nbdevup, nbdevdn (default=20,2,2)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D CLOSE {'>'} 1D BBANDS_MIDDLE(20,2,2)</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>BBANDS_LOWER (Bollinger Bands Lower Band)</Typography>
                <Typography variant="body2" paragraph>Lower volatility band (SMA - standard deviations).</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period, nbdevup, nbdevdn (default=20,2,2)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D CLOSE {'<'} 1D BBANDS_LOWER(20,2,2)</Typography>
              </Paper>
            </Box>

            <Typography variant="h6">Momentum Indicators</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ml: 2 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>RSI (Relative Strength Index)</Typography>
                <Typography variant="body2" paragraph>Measures the speed and magnitude of recent price changes (0-100).</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period (default=14)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D RSI(14) {'>'} 70, 1D RSI(14) {'<'} 30</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>MACD (MACD Line)</Typography>
                <Typography variant="body2" paragraph>Main line of Moving Average Convergence Divergence.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: fastperiod, slowperiod, signalperiod (default=12,26,9)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D MACD(12,26,9) {'>'} 0</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>MACD_SIGNAL (MACD Signal Line)</Typography>
                <Typography variant="body2" paragraph>9-period EMA of MACD Line, used for crossover signals.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: fastperiod, slowperiod, signalperiod (default=12,26,9)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D MACD(12,26,9) {'>'} 1D MACD_SIGNAL(12,26,9)</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>MACD_HIST (MACD Histogram)</Typography>
                <Typography variant="body2" paragraph>Difference between MACD Line and Signal Line.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: fastperiod, slowperiod, signalperiod (default=12,26,9)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D MACD_HIST(12,26,9) {'>'} 0</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>STOCH_K (Stochastic %K)</Typography>
                <Typography variant="body2" paragraph>Fast stochastic oscillator comparing close to price range.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: fastk_period, slowk_period, slowd_period (default=5,3,3)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D STOCH_K(5,3,3) {'>'} 80</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>STOCH_D (Stochastic %D)</Typography>
                <Typography variant="body2" paragraph>Slow stochastic oscillator, smoothed version of %K.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: fastk_period, slowk_period, slowd_period (default=5,3,3)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D STOCH_K(5,3,3) {'>'} 1D STOCH_D(5,3,3)</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>CCI (Commodity Channel Index)</Typography>
                <Typography variant="body2" paragraph>Momentum oscillator measuring current price level relative to average.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period (default=14)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D CCI(14) {'>'} 100, 1D CCI(20) {'<'} -100</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>MOM (Momentum)</Typography>
                <Typography variant="body2" paragraph>Rate of change in price over specified period.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period (default=10)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D MOM(10) {'>'} 0</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>ROC (Rate of Change)</Typography>
                <Typography variant="body2" paragraph>Percentage rate of change between current and n-periods ago.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period (default=10)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D ROC(10) {'>'} 5</Typography>
              </Paper>
            </Box>

            <Typography variant="h6">Volatility Indicators</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ml: 2 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>ATR (Average True Range)</Typography>
                <Typography variant="body2" paragraph>Measures market volatility by decomposing the entire range of an asset price.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period (default=14)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D ATR(14) {'>'} 50, 1H ATR(20) {'>'} 1H ATR(20)[5]</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>NATR (Normalized Average True Range)</Typography>
                <Typography variant="body2" paragraph>ATR expressed as percentage of closing price.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period (default=14)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D NATR(14) {'>'} 3.0</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>TRANGE (True Range)</Typography>
                <Typography variant="body2" paragraph>Current period's true range without smoothing.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: none</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D TRANGE() {'>'} 1D ATR(14)</Typography>
              </Paper>
            </Box>

            <Typography variant="h6">Trend Indicators</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ml: 2 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>SUPERTREND (Supertrend)</Typography>
                <Typography variant="body2" paragraph>Trend following indicator combining ATR and median price.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period, multiplier (default=10,3)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D SUPERTREND(10,3) {'<'} 1D CLOSE</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>SUPERTREND_UPPER (Supertrend Upper Band)</Typography>
                <Typography variant="body2" paragraph>Upper band of Supertrend indicator based on ATR.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period, multiplier (default=10,3)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D CLOSE {'>'} 1D SUPERTREND_UPPER(10,3)</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>SUPERTREND_LOWER (Supertrend Lower Band)</Typography>
                <Typography variant="body2" paragraph>Lower band of Supertrend indicator based on ATR.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period, multiplier (default=10,3)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D CLOSE {'<'} 1D SUPERTREND_LOWER(10,3)</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>ADX (Average Directional Movement Index)</Typography>
                <Typography variant="body2" paragraph>Measures trend strength regardless of direction.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period (default=14)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D ADX(14) {'>'} 25</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>AROON (Aroon Oscillator)</Typography>
                <Typography variant="body2" paragraph>Identifies trend changes and strength.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: period (default=14)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D AROON(14) {'>'} 0</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>SAR (Parabolic SAR)</Typography>
                <Typography variant="body2" paragraph>Stop and reverse indicator for trend following.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: acceleration, maximum (default=0.02,0.2)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D CLOSE {'>'} 1D SAR(0.02,0.2)</Typography>
              </Paper>
            </Box>

            <Typography variant="h6">Volume Indicators</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ml: 2 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>OBV (On Balance Volume)</Typography>
                <Typography variant="body2" paragraph>Measures buying and selling pressure as a cumulative indicator.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: none</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D OBV() {'>'} 1D OBV()[5]</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>AD (Accumulation/Distribution Line)</Typography>
                <Typography variant="body2" paragraph>Volume indicator showing money flow direction.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: none</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D AD() {'>'} 1D AD()[1]</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>ADOSC (Chaikin A/D Oscillator)</Typography>
                <Typography variant="body2" paragraph>Momentum indicator based on the accumulation/distribution line.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: fastperiod, slowperiod (default=3,10)</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D ADOSC(3,10) {'>'} 0</Typography>
              </Paper>
            </Box>

            <Typography variant="h6">Pattern Recognition</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ml: 2 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>CDLDOJI (Doji Pattern)</Typography>
                <Typography variant="body2" paragraph>Identifies doji candlestick patterns showing market indecision.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: none</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D CDLDOJI() != 0</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>CDLENGULFING (Engulfing Pattern)</Typography>
                <Typography variant="body2" paragraph>Identifies bullish/bearish engulfing patterns.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: none</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D CDLENGULFING() {'>'} 0</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>CDLHAMMER (Hammer Pattern)</Typography>
                <Typography variant="body2" paragraph>Identifies hammer candlestick patterns.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: none</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D CDLHAMMER() {'>'} 0</Typography>
              </Paper>
            </Box>

            <Typography variant="h6">Price Transform</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ml: 2 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>AVGPRICE (Average Price)</Typography>
                <Typography variant="body2" paragraph>Average of OHLC prices.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: none</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D CLOSE {'>'} 1D AVGPRICE()</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>TYPPRICE (Typical Price)</Typography>
                <Typography variant="body2" paragraph>Typical price (High + Low + Close) / 3.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: none</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D TYPPRICE() {'>'} 1D SMA(20)</Typography>
              </Paper>
            </Box>

            <Typography variant="h6">Base Data Types</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ml: 2 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Price Data: CLOSE, OPEN, HIGH, LOW</Typography>
                <Typography variant="body2" paragraph>Basic price data available for all timeframes.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: none</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D CLOSE {'>'} 1500, 1H HIGH {'>'} 1H HIGH[1]</Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>VOLUME</Typography>
                <Typography variant="body2" paragraph>Trading volume data.</Typography>
                <Typography variant="body2" color="text.secondary">Parameters: none</Typography>
                <Typography variant="caption" display="block" color="text.secondary">Examples: 1D VOLUME {'>'} 1000000</Typography>
              </Paper>
            </Box>

            <Typography variant="h6">Supported Timeframes</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ml: 2 }}>
              <Chip label="1MIN" variant="outlined" />
              <Chip label="5MIN" variant="outlined" />
              <Chip label="15MIN" variant="outlined" />
              <Chip label="1H" variant="outlined" />
              <Chip label="4H" variant="outlined" />
              <Chip label="1D" variant="outlined" />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default IndicatorHelp;